import { AlertTriangle, Loader2, RefreshCw, WifiOff } from 'lucide-react'
import { classifyError, type ErrorKind } from '../../lib/errorKind'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { cn } from '../../lib/utils'

/**
 * Shared loading / failure surfaces.
 *
 * The guiding rule: never blame the user's connection for a server's mistake,
 * and never say "something went wrong" when the honest answer is "you're in a
 * tunnel". `ErrorState` picks the wording from the error it was handed plus
 * live connectivity, so callers don't have to classify anything themselves.
 */

function errorMessage(error: unknown): string | null {
  if (!error) return null
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  return null
}

const COPY: Record<ErrorKind, { title: string; hint: string }> = {
  offline: {
    title: "You're offline",
    hint: 'This page will load as soon as you reconnect.',
  },
  unreachable: {
    title: "Can't reach the server",
    hint: 'Your connection looks fine, so this is probably on our side.',
  },
  error: {
    title: "Couldn't load this",
    hint: 'Something went wrong while fetching this page.',
  },
}

interface ErrorStateProps {
  /** The thrown value, a message string, or null. */
  error?: unknown
  /** Retry handler — omit to hide the button (e.g. auto-retrying already). */
  onRetry?: () => void
  /** True while a retry is in flight. */
  retrying?: boolean
  /** Force a kind instead of inferring it. */
  kind?: ErrorKind
  /** Compact inline variant for panels and drawers. */
  inline?: boolean
  className?: string
}

/**
 * Full-width failure panel with a retry affordance.
 *
 * While offline it also states that the retry happens automatically, because
 * `useRetryOnReconnect` refetches on reconnect — a user who taps "Try again"
 * ten times in a dead zone should be told the app is already handling it.
 */
export function ErrorState({
  error,
  onRetry,
  retrying = false,
  kind,
  inline = false,
  className,
}: ErrorStateProps) {
  const { offline, checking, recheck } = useOnlineStatus()
  const resolved = kind ?? classifyError(error, offline)
  const copy = COPY[resolved]
  const detail = errorMessage(error)
  const Icon = resolved === 'error' ? AlertTriangle : WifiOff

  const handleRetry = () => {
    // Re-probe as well: if the user was offline, the fastest path back to a
    // working page is confirming the network before refetching.
    void recheck()
    onRetry?.()
  }

  const busy = retrying || checking

  if (inline) {
    return (
      <div
        role="status"
        className={cn(
          'flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm text-muted-foreground',
          className,
        )}
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{copy.title}</span>
        {onRetry && (
          <button
            type="button"
            onClick={handleRetry}
            disabled={busy}
            className="flex-shrink-0 rounded px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-60"
          >
            {busy ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-border bg-card px-6 py-12 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'rounded-full p-3',
          resolved === 'error'
            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-300'
            : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <h2 className="mt-4 text-base font-semibold text-foreground">{copy.title}</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{copy.hint}</p>
      {detail && resolved === 'error' && (
        <p className="mt-2 max-w-sm break-words text-xs text-muted-foreground/80">{detail}</p>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={busy}
          className="mt-5 inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} aria-hidden="true" />
          {busy ? 'Retrying…' : 'Try again'}
        </button>
      )}
      {resolved === 'offline' && (
        <p className="mt-3 text-xs text-muted-foreground">Retrying automatically.</p>
      )}
    </div>
  )
}

/**
 * Small spinner for buttons and inline affordances, where a skeleton would be
 * more disruptive than the wait it covers.
 */
export function InlineSpinner({
  label = 'Loading',
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-muted-foreground', className)}>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  )
}

/**
 * Centred spinner with a label, for short waits inside an already-rendered
 * container (dropdown panels, popovers) where a shaped skeleton would be
 * overkill. Prefer a skeleton for anything that occupies a page.
 */
export function LoadingRow({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground', className)}
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  )
}

/**
 * Dims already-rendered content while a background refresh runs, instead of
 * throwing it away and showing a spinner. Keeping stale data on screen is
 * almost always better than a blank frame.
 */
export function RefreshingOverlay({ active }: { active: boolean }) {
  if (!active) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none absolute right-3 top-3 z-10 inline-flex items-center gap-2 rounded-full border border-border bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur-sm"
    >
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      Refreshing
    </div>
  )
}
